import React, { useEffect, useRef, useState } from 'react';

import type { SxProps, Theme } from '@mui/material/styles';

import { Input } from 'ui/input';
import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import IconButton from 'components/partials/button/icon-button';
import Tooltip from 'components/partials/tooltip/tooltip';

import { getTruncatedDisplay } from 'helpers/string.helpers';

import {
  AID_MODEL_NAME_DISPLAY_MAX_LENGTH,
  AID_MODEL_NAME_MAX_LENGTH,
} from '../../aid-studio-details.constants';

import Icons from 'assets/icons';

const INLINE_TITLE_INPUT_SX: SxProps<Theme> = (theme) => ({
  fontSize: theme.typography.h3.fontSize,
  lineHeight: theme.typography.h3.lineHeight,
  '& input': { py: 0.25 },
});

interface InlineEditableTitleProps {
  name: string;
  canEdit: boolean;
  isSaving?: boolean;
  onRename: (name: string) => Promise<void>;
}

const InlineEditableTitle = ({
  name,
  canEdit,
  isSaving,
  onRename,
}: InlineEditableTitleProps): React.JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(name);
  }, [name]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const trimmed = value.trim();
  const isConfirmDisabled =
    !trimmed || trimmed.length > AID_MODEL_NAME_MAX_LENGTH || isSaving;

  const handleCancel = (): void => {
    setValue(name);
    setIsEditing(false);
  };

  const handleConfirm = async (): Promise<void> => {
    if (isConfirmDisabled) return;

    if (trimmed === name) {
      setIsEditing(false);

      return;
    }

    await onRename(trimmed);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={AID_MODEL_NAME_MAX_LENGTH}
        showCharCount={false}
        aria-label="Model name"
        size="medium"
        controlSx={INLINE_TITLE_INPUT_SX}
        endAdornment={
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="xs"
              variant="reserve-text"
              icon={<Icons.Check />}
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              aria-label="Confirm model name"
              data-testid="confirm-model-name-button"
            />
            <span
              className="h-5 w-px shrink-0 bg-gray-300"
              aria-hidden="true"
            />
            <IconButton
              size="xs"
              variant="reserve-text"
              icon={<Icons.Close />}
              onClick={handleCancel}
              aria-label="Cancel renaming model"
              data-testid="cancel-model-name-button"
            />
          </Stack>
        }
        data-testid="model-name-inline-input"
      />
    );
  }

  const { displayText, isTruncated } = getTruncatedDisplay(
    name,
    AID_MODEL_NAME_DISPLAY_MAX_LENGTH
  );

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Tooltip
        position="top-end"
        content={name}
        enabled={isTruncated}
        anchorStrategy="child"
      >
        <Typography
          data-testid="aid-studio-model-name-heading"
          variant="h3"
          component="h1"
          sx={{ flexShrink: 0 }}
        >
          {displayText}
        </Typography>
      </Tooltip>

      {canEdit && (
        <IconButton
          size="sm"
          variant="reserve-text"
          icon={<Icons.EditPencil />}
          onClick={() => setIsEditing(true)}
          aria-label="Rename model"
          data-testid="rename-model-button"
        />
      )}
    </Stack>
  );
};

export default InlineEditableTitle;
